import { useState } from 'react';
import type { FormEvent } from 'react';

import { Link } from 'react-router';

import { Avviso, Campo, Pulsante } from '../../componenti';
import { ErroreApi, ErroreRete } from '../../lib/api';
import { dataEOra, useFormato } from '../../lib/formato';
import { useSessione } from '../../stato/Sessione';
import { Loghi, Marchio, useIntestazione } from './Marchio';
import { UtenzaDisattivata, comeDisattivazione } from './UtenzaDisattivata';
import type { Disattivazione } from './UtenzaDisattivata';

/**
 * Schermate di accesso 6a–6e.
 *
 * 6a telefono · 6b tablet · 6c desktop — stesso modulo, tre composizioni.
 * 6d credenziali errate · 6e utenza disattivata.
 *
 * Nessuna registrazione autonoma: le credenziali arrivano dal PDF emesso
 * dall'amministratore.
 */

const TENTATIVI_MASSIMI = 5;

export function Accesso() {
  const formato = useFormato();
  const intestazione = useIntestazione();
  const { entra } = useSessione();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errore, setErrore] = useState<ErroreApi | ErroreRete | null>(null);
  const [tentativi, setTentativi] = useState(0);
  const [disattivata, setDisattivata] = useState<Disattivazione | null>(null);
  const [inCorso, setInCorso] = useState(false);

  async function invia(e: FormEvent) {
    e.preventDefault();
    if (inCorso) return;
    setInCorso(true);
    setErrore(null);
    try {
      await entra(username.trim(), password);
    } catch (err) {
      const spenta = comeDisattivazione(err);
      if (spenta) {
        setDisattivata(spenta);
      } else if (err instanceof ErroreApi || err instanceof ErroreRete) {
        setErrore(err);
        // Il conteggio è solo locale: il server non espone quanti tentativi restino.
        if (err instanceof ErroreApi && err.codice === 'credenziali_non_valide') {
          setTentativi((n) => Math.min(n + 1, TENTATIVI_MASSIMI));
        }
        setPassword('');
      } else {
        throw err;
      }
    } finally {
      setInCorso(false);
    }
  }

  if (disattivata) {
    return (
      <UtenzaDisattivata
        dati={disattivata}
        onTorna={() => {
          setDisattivata(null);
          setPassword('');
        }}
      />
    );
  }

  const credenzialiErrate =
    errore instanceof ErroreApi && errore.codice === 'credenziali_non_valide';

  const campi = (
    <>
      <Campo
        etichetta="Nome utente"
        name="username"
        autoComplete="username"
        autoCapitalize="none"
        autoCorrect="off"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
      />
      <Campo
        etichetta="Password"
        name="password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        invalido={credenzialiErrate}
        aiuto={
          credenzialiErrate && tentativi > 0
            ? `Tentativo ${tentativi} di ${TENTATIVI_MASSIMI}.`
            : undefined
        }
      />
      <Pulsante type="submit" largo grande disabled={inCorso}>
        Entra
      </Pulsante>
    </>
  );

  const avviso = errore ? (
    <Avviso
      tono="errore"
      titolo={
        errore instanceof ErroreRete ? 'Serve la connessione' : errore.message
      }
    >
      {errore instanceof ErroreRete
        ? 'Controlla la rete e riprova.'
        : credenzialiErrate
          ? `Riprova. Dopo cinque tentativi falliti l’accesso si blocca per 15 minuti.`
          : null}
    </Avviso>
  ) : null;

  /*
   * La via per chi non ha un'utenza.
   *
   * La situazione dei campi è pubblica di proposito: dice quali campi sono liberi adesso,
   * e a quella domanda si può rispondere senza sapere chi sta chiedendo. Finora però ci si
   * arrivava solo digitando `/situazione` a mano — che equivale a non esserci, per chiunque
   * non lo abbia già sentito dire da qualcuno.
   *
   * È un collegamento e non un `Pulsante`, benché ne porti l'aspetto: porta a un altro
   * indirizzo, quindi deve potersi aprire in una scheda nuova, copiare e salvare fra i
   * preferiti — cosa che un `<button>` non concede. La variante neutra lo tiene sotto
   * l'«Entra», che resta l'azione principale della schermata.
   */
  const situazione = (
    <Link
      className="pulsante pulsante--neutro pulsante--grande pulsante--largo accesso__situazione"
      to="/situazione"
    >
      Situazione campi
    </Link>
  );

  /*
   * R-51 — la via dell'ospite, sotto quella della situazione dei campi.
   *
   * Sono le due sole cose che si possono fare da qui senza avere un'utenza, e stanno
   * insieme per questo. Compare solo quando l'amministratore ha aperto il modulo: una voce
   * che porta a una porta chiusa è peggio di una voce che non c'è, perché chi la preme
   * scopre di aver perso un giro e non sa se riprovare.
   *
   * `ospiti_aperti` è indefinito finché l'intestazione non è arrivata, e in quel momento la
   * voce non si disegna: un pulsante che compare mezzo secondo dopo gli altri fa saltare la
   * schermata sotto il pollice di chi sta già premendo «Entra».
   */
  const ospite = intestazione.ospiti_aperti ? (
    <Link
      className="pulsante pulsante--neutro pulsante--grande pulsante--largo accesso__situazione"
      to="/ospite"
    >
      Sono un ospite: inserisco i miei dati
    </Link>
  ) : null;

  if (formato === 'mobile') {
    return (
      <div className="accesso accesso--colonna" data-densita="tattile">
        <Marchio intestazione={intestazione} />
        {avviso}
        <form className="accesso__scheda" onSubmit={invia} noValidate>
          {campi}
        </form>
        {credenzialiErrate ? (
          <div className="accesso__nota">
            La segreteria può rigenerare la password e stampare un nuovo PDF.
          </div>
        ) : null}
        {situazione}
        {ospite}
      </div>
    );
  }

  const tablet = formato === 'tablet';

  return (
    <div
      className={`accesso accesso--${formato}`}
      data-densita={tablet ? 'tattile' : 'compatta'}
    >
      <div className="accesso__telaio">
        <aside className="accesso__pannello">
          <div className="accesso__pannello-testa">
            <Loghi />
            <div>
              <div className="accesso__pannello-nome">{intestazione.denominazione}</div>
              <div className="accesso__pannello-sottotitolo">
                {tablet ? 'ACCETTAZIONE' : 'AMMINISTRAZIONE'}
              </div>
            </div>
          </div>
          {/* Resta l'ora, che al tablet in sala serve davvero. La descrizione di ciò che
              la postazione sa fare è stata tolta: chi ci sta davanti lo scopre entrando. */}
          {tablet ? (
            <div className="accesso__pannello-coda">
              <div className="accesso__pannello-data">{dataEOra()}</div>
            </div>
          ) : null}
        </aside>

        <div className="accesso__corpo">
          {/* `.accesso__corpo` è una riga: senza questa colonna il collegamento si
              sistemava accanto al modulo invece che sotto l'«Entra». */}
          <div className="accesso__colonna">
            <form className="accesso__modulo" onSubmit={invia} noValidate>
              <div className="accesso__titolo">Accesso</div>
              {avviso}
              {campi}
            </form>
            {situazione}
            {ospite}
          </div>
        </div>
      </div>
    </div>
  );
}
